import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSidebar } from "@fortawesome/pro-light-svg-icons";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import DataTable from "components/dataTable/DataTable";
import Icon from "components/icon/Icon";
import Callout from "components/callout/Callout";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { SkeletonBar } from "components/skeleton";
import AddFolderProductsSkeletonRow from "components/folders/AddFolderProductsSkeletonRow";
import { IDepartmentBase, IDepartments, IProduct, ICategories, IVariant } from "redux/reducers/models/product";
import ProductBrowseTree from "components/folders/ProductBrowseTree";
import {
  countFolderProductsMatchingFilter,
  IBrowseFilter,
  productMatchesBrowseFilter,
} from "components/folders/productBrowseFilters";
import { LocaleCurrency } from "helpers/Locale";
import { useAppSelector } from "hooks/redux";
import "./addFolderProductsModal.scss";

function getDepartmentTitle(department: IDepartmentBase | null | undefined) {
  if (!department) {
    return undefined;
  }

  const trimmed = department.title?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/** Price for the variant used by batch sell (first sellable variant, with facility override). */
function getProductDisplayPrice(product: IProduct): number | null {
  const variant = product.variants?.[0];
  if (!variant) {
    return null;
  }

  const override = variant.inventory_item?.inventory_level?.price_override;
  if (override != null && Number.isFinite(Number(override))) {
    return Number(override);
  }

  if (variant.price != null && Number.isFinite(Number(variant.price))) {
    return Number(variant.price);
  }

  return null;
}

function getBrowseEmptyTypeLabel(browseType: IBrowseFilter["type"], t: (key: string) => string) {
  if (browseType === "department") {
    return t("department");
  }

  if (browseType === "category") {
    return t("category");
  }

  if (browseType === "subcategory") {
    return t("subcategory");
  }

  return null;
}

const searchInputId = "add-folder-products-modal-search";

function handleActivationKeyDown(event: React.KeyboardEvent, action: () => void) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
}

interface IAddFolderProductsModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (products: IProduct[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  productsToAdd: IProduct[];
  availableSlotCount: number;
  totalEmptySlotCount: number;
  folderProducts: IProduct[];
  productSearchResults: IProduct[] | undefined;
  productSearchTotalCount?: number;
  productSearchPending: boolean;
  productSearchLoadingMore: boolean;
  productSearchSelectAllPending?: boolean;
  onLoadMoreProducts: () => void;
  onSelectAllMatchingProducts: () => void;
  onSelectProduct: (product: IProduct | IProduct[]) => void;
  onUnselectProduct: (product: IProduct | IProduct[]) => void;
  departments: Array<IDepartments & { cateogries?: ICategories[] }>;
  departmentsLoading?: boolean;
  uncategorizedCount: number;
  browseFilter: IBrowseFilter;
  onBrowseFilterChange: (filter: IBrowseFilter) => void;
  onCreateProduct?: () => void;
  onGoToProducts?: () => void;
  /** Override sheet title (defaults to "Add products") */
  title?: string;
  /** Override sheet support text */
  supportText?: string;
  /** Override primary action label when confirming selection */
  confirmText?: string;
  /** Show quantity inputs (e.g. batch sell) */
  showQuantity?: boolean;
  productQuantities?: Record<number, number>;
  onQuantityChange?: (productId: number, quantity: number) => void;
  /** Show sellable variant price (e.g. batch sell) */
  showPrice?: boolean;
  /** Folder capacity copy (hide for non-folder flows like batch sell) */
  showSlotUsage?: boolean;
  okLoading?: boolean;
  /** Hide folder slot capacity UI (pricing sheets). */
  hideSlots?: boolean;
  /** Allow expand + select individual variants or all variants on a product. */
  enableVariantSelection?: boolean;
  /** Variant IDs already assigned (hidden / treated as added when all are excluded). */
  excludedVariantIds?: number[];
  vendorId?: number;
  vendorFilterEnabled?: boolean;
  onVendorFilterChange?: (enabled: boolean) => void;
  /**Open browse view on open, even when productsToAdd has items in it */
  browseOnOpen?: boolean;
}

export default function AddFolderProductsModal(props: IAddFolderProductsModalProps) {
  const {
    open,
    onClose,
    onAdd,
    search,
    onSearchChange,
    productsToAdd,
    availableSlotCount,
    totalEmptySlotCount,
    folderProducts,
    productSearchResults,
    productSearchTotalCount,
    productSearchPending,
    productSearchLoadingMore,
    productSearchSelectAllPending = false,
    onLoadMoreProducts,
    onSelectAllMatchingProducts,
    onSelectProduct,
    onUnselectProduct,
    departments,
    departmentsLoading = false,
    uncategorizedCount,
    browseFilter,
    onBrowseFilterChange,
    onCreateProduct,
    onGoToProducts,
    title,
    supportText,
    confirmText,
    showQuantity = false,
    productQuantities = {},
    onQuantityChange,
    showPrice = false,
    showSlotUsage = true,
    okLoading = false,
    hideSlots = false,
    enableVariantSelection = false,
    excludedVariantIds,
    vendorId,
    vendorFilterEnabled,
    onVendorFilterChange,
    browseOnOpen,
  } = props;

  const { t } = useTranslation();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const currency = facilityStore?.facility?.default_currency_code;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLTableRowElement>(null);
  const prevLoadedCountRef = useRef(0);
  const wasLoadingMoreRef = useRef(false);
  const [loadMoreCompleteMessage, setLoadMoreCompleteMessage] = useState("");
  const [viewMode, setViewMode] = useState<"browse" | "review">("browse");
  const [reviewUncheckedIds, setReviewUncheckedIds] = useState<Set<number>>(new Set());
  const [browseOpen, setBrowseOpen] = useState(false);
  const [selectAllPending, setSelectAllPending] = useState(false);
  const [collapsedProductIds, setCollapsedProductIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) {
      setViewMode("browse");
      setReviewUncheckedIds(new Set());
      setBrowseOpen(false);
      setSelectAllPending(false);
      setCollapsedProductIds(new Set());
      return;
    }
    if (browseOnOpen) {
      setViewMode("browse");
      return;
    }
    
    // Prefill (e.g. loading an order draft) should land on review so selections are visible.
    if (productsToAdd.length > 0) {
      setViewMode("review");
    }
  }, [open]);

  useEffect(() => {
    if (viewMode === "review" && productsToAdd.length === 0) {
      setViewMode("browse");
    }
  }, [viewMode, productsToAdd.length]);

  useEffect(() => {
    if (!open || viewMode !== "browse") {
      return;
    }

    const timeoutId = setTimeout(() => {
      const input = searchInputRef.current;
      if (!input) {
        return;
      }
      input.focus();
      input.select();
    }, 0);


    return () => clearTimeout(timeoutId);
  }, [open, viewMode]);

  const filteredProducts = useMemo(() => productSearchResults ?? [], [productSearchResults]);

  const folderProductIds = useMemo(() => new Set(folderProducts.map(product => product.id)), [folderProducts]);
  const selectedProductIds = useMemo(() => new Set(productsToAdd.map(product => product.id)), [productsToAdd]);
  const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds ?? []), [excludedVariantIds]);
  const selectedProductsById = useMemo(() => {
    const map = new Map<number, IProduct>();
    productsToAdd.forEach(product => map.set(product.id, product));
    return map;
  }, [productsToAdd]);

  function getAvailableVariants(product: IProduct): IVariant[] {
    const variants = product.variants ?? [];
    if (!enableVariantSelection || excludedVariantIdSet.size === 0) {
      return variants;
    }
    return variants.filter(variant => !excludedVariantIdSet.has(variant.id));
  }

  function getDisplayVariants(product: IProduct): IVariant[] {
    return product.variants ?? [];
  }

  function isVariantAlreadyAdded(variantId: number) {
    return enableVariantSelection && excludedVariantIdSet.has(variantId);
  }

  function isProductFullyAdded(product: IProduct) {
    if (folderProductIds.has(product.id)) {
      return true;
    }

    if (!enableVariantSelection) {
      return false;
    }

    const variants = product.variants ?? [];
    if (variants.length === 0) {
      return false;
    }

    return getAvailableVariants(product).length === 0;
  }

  function getSelectedVariants(product: IProduct): IVariant[] {
    return selectedProductsById.get(product.id)?.variants ?? [];
  }

  function areAllAvailableVariantsSelected(product: IProduct) {
    const available = getAvailableVariants(product);
    if (available.length === 0) {
      return isProductFullyAdded(product);
    }

    const selectedIds = new Set(getSelectedVariants(product).map(variant => variant.id));
    return available.every(variant => selectedIds.has(variant.id));
  }

  function areSomeAvailableVariantsSelected(product: IProduct) {
    const available = getAvailableVariants(product);
    if (available.length === 0) {
      return false;
    }

    const selectedIds = new Set(getSelectedVariants(product).map(variant => variant.id));
    const selectedCount = available.filter(variant => selectedIds.has(variant.id)).length;
    return selectedCount > 0 && selectedCount < available.length;
  }

  function isVariantSelected(product: IProduct, variantId: number) {
    if (isVariantAlreadyAdded(variantId)) {
      return true;
    }

    return getSelectedVariants(product).some(variant => variant.id === variantId);
  }

  function getVariantCountLabel(variantCount: number) {
    return variantCount === 1 ? t("1 variant") : t("{{count}} variants", { count: variantCount });
  }

  function isProductExpanded(productId: number) {
    return !collapsedProductIds.has(productId);
  }

  function toggleProductExpanded(productId: number) {
    setCollapsedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  const usedSlots = folderProducts.length + productsToAdd.length;
  const totalSlots = folderProducts.length + totalEmptySlotCount;
  const remainingAssignableSlots = Math.max(0, availableSlotCount - productsToAdd.length);
  const remainingEmptySlots = Math.max(0, totalEmptySlotCount - productsToAdd.length);
  const isFolderCapacityFull = remainingAssignableSlots === 0;
  const folderMatchingCount = useMemo(
    () => countFolderProductsMatchingFilter(folderProducts, browseFilter),
    [folderProducts, browseFilter],
  );
  const fullyAddedMatchingCount = useMemo(() => {
    if (!enableVariantSelection) {
      return folderMatchingCount;
    }

    return filteredProducts.filter(product => isProductFullyAdded(product)).length;
  }, [enableVariantSelection, filteredProducts, folderMatchingCount, excludedVariantIdSet, folderProductIds]);
  const totalSelectable = Math.max(
    0,
    (productSearchTotalCount ?? 0) - (enableVariantSelection ? fullyAddedMatchingCount : folderMatchingCount),
  );
  const slotsHidden = hideSlots || !showSlotUsage;
  const showSlotsCapacityCallout = !slotsHidden && productsToAdd.length > 0 && totalSelectable > totalEmptySlotCount;
  const matchingSelectedCount = useMemo(() => {
    if (browseFilter.type === "all") {
      return productsToAdd.length;
    }

    return productsToAdd.filter(product => productMatchesBrowseFilter(product, browseFilter)).length;
  }, [productsToAdd, browseFilter]);
  const maxSelectableInFilter = slotsHidden
    ? totalSelectable
    : Math.min(totalSelectable, availableSlotCount - (productsToAdd.length - matchingSelectedCount));
  const allSelectableSelected = totalSelectable > 0 && matchingSelectedCount === maxSelectableInFilter;
  const someSelectableSelected = matchingSelectedCount > 0 && !allSelectableSelected;
  const selectAllVisualChecked = allSelectableSelected || selectAllPending || productSearchSelectAllPending;
  const selectAllVisualIndeterminate = someSelectableSelected && !selectAllVisualChecked;

  useEffect(() => {
    if (allSelectableSelected) {
      setSelectAllPending(false);
    }
  }, [allSelectableSelected]);

  useEffect(() => {
    if (!productSearchSelectAllPending && selectAllPending && !allSelectableSelected) {
      setSelectAllPending(false);
    }
  }, [productSearchSelectAllPending, selectAllPending, allSelectableSelected]);

  const isInitialLoading = productSearchResults === undefined;
  const showSkeletonLoading = isInitialLoading || (productSearchPending && filteredProducts.length === 0);
  const isRefreshing = productSearchPending && filteredProducts.length > 0;
  const isTableEmpty = !showSkeletonLoading && filteredProducts.length === 0;
  const hasMoreProducts =
    productSearchTotalCount != null &&
    productSearchResults != null &&
    productSearchResults.length < productSearchTotalCount;

  const browseTableColumns = useMemo(() => {
    const showStatus = showSlotUsage;
    const statusColumn = showStatus
      ? [
          {
            label: t("Status"),
            content: <span className="add-folder-products-modal__visually-hidden">{t("Status")}</span>,
            width: "7rem",
          },
        ]
      : [];
    const priceColumn = showPrice ? [{ label: t("Price"), width: "5.5rem" }] : [];
    const quantityColumn = showQuantity ? [{ label: t("Qty"), width: "6rem" }] : [];

    return [
      { label: t("Name") },
      { label: t("Type"), width: "8rem" },
      ...priceColumn,
      ...quantityColumn,
      ...statusColumn,
    ];
  }, [showPrice, showQuantity, showSlotUsage, t]);
  const skeletonRowKeys = [
    "folder-product-skeleton-0",
    "folder-product-skeleton-1",
    "folder-product-skeleton-2",
    "folder-product-skeleton-3",
    "folder-product-skeleton-4",
    "folder-product-skeleton-5",
  ];
  const loadMoreSkeletonRowKeys = ["folder-product-load-more-skeleton-0", "folder-product-load-more-skeleton-1"];

  function renderProductPrice(product: IProduct) {
    const price = getProductDisplayPrice(product);
    if (price == null) {
      return "—";
    }
    return <LocaleCurrency currency={currency} amount={price} />;
  }

  function variantQuantity(variant: IVariant | undefined) {
    const onHand = variant?.inventory_item?.inventory_level?.quantity_available;
    const quantity = onHand ?? variant?.quantity;
    if (quantity == null || Number.isNaN(Number(quantity))) {
      return "—";
    }
    return String(Number(quantity));
  }

  function productQuantity(product: IProduct) {
    const variants = product.variants ?? [];
    if (variants.length !== 1) {
      return "—";
    }
    return variantQuantity(variants[0]);
  }

  function isProductInFolder(product: IProduct) {
    return isProductFullyAdded(product);
  }

  function isProductSelected(product: IProduct) {
    if (enableVariantSelection) {
      return areAllAvailableVariantsSelected(product);
    }

    return selectedProductIds.has(product.id);
  }

  function getProductHierarchyLabel(product: IProduct) {
    const department = getDepartmentTitle(product.department);
    const category = getDepartmentTitle(product.category);
    const subtitle = product.subtitle?.trim();

    if (!department) {
      return t("Uncategorized");
    }

    if (!category) {
      return department;
    }

    if (subtitle) {
      return `${department} > ${category} • ${subtitle}`;
    }

    return `${department} > ${category}`;
  }

  function getProductDisplayTitle(product: IProduct) {
    return product.preferred_title || product.title;
  }

  function handleSelectAllToggle() {
    if (allSelectableSelected) {
      setSelectAllPending(false);
      onUnselectProduct(productsToAdd.filter(product => productMatchesBrowseFilter(product, browseFilter)));
      return;
    }

    setSelectAllPending(true);
    onSelectAllMatchingProducts();
  }

  function handleClearAll() {
    if (productsToAdd.length === 0) {
      return;
    }

    onUnselectProduct(productsToAdd);
  }

  function handleRowClick(product: IProduct) {
    if (isProductInFolder(product)) {
      return;
    }

    if (enableVariantSelection) {
      const availableVariants = getAvailableVariants(product);
      if (availableVariants.length === 0) {
        return;
      }

      if (areAllAvailableVariantsSelected(product)) {
        onUnselectProduct(product);
        return;
      }

      onSelectProduct({ ...product, variants: availableVariants, variant_count: availableVariants.length });
      return;
    }

    if (isProductSelected(product)) {
      onUnselectProduct(product);
      return;
    }

    onSelectProduct(product);
  }

  function handleVariantRowClick(product: IProduct, variant: IVariant) {
    if (isVariantAlreadyAdded(variant.id)) {
      return;
    }

    if (isProductInFolder(product)) {
      return;
    }

    if (isVariantSelected(product, variant.id)) {
      onUnselectProduct({ ...product, variants: [variant] });
      return;
    }

    onSelectProduct({ ...product, variants: [variant], variant_count: product.variant_count });
  }

  function isReviewChecked(product: IProduct) {
    return !reviewUncheckedIds.has(product.id);
  }

  function handleReviewRowClick(product: IProduct) {
    setReviewUncheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.add(product.id);
      }
      return next;
    });
  }

  function applyReviewChanges() {
    const toRemove = productsToAdd.filter(product => reviewUncheckedIds.has(product.id));
    if (toRemove.length > 0) {
      onUnselectProduct(toRemove);
    }
    setReviewUncheckedIds(new Set());
  }

  function handleOpenReview() {
    setReviewUncheckedIds(new Set());
    setBrowseOpen(false);
    setViewMode("review");
  }

  function handleReviewClearAll() {
    setReviewUncheckedIds(new Set(productsToAdd.map(product => product.id)));
  }

  function handleSaveAndGoBack() {
    applyReviewChanges();
    setViewMode("browse");
  }

  useEffect(() => {
    if (!open || viewMode !== "browse" || !hasMoreProducts || productSearchLoadingMore || productSearchPending) {
      return;
    }

    const scrollRoot = tableScrollRef.current;
    const sentinel = loadMoreSentinelRef.current;

    if (!scrollRoot || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          onLoadMoreProducts();
        }
      },
      { root: scrollRoot, rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    open,
    viewMode,
    hasMoreProducts,
    productSearchLoadingMore,
    productSearchPending,
    onLoadMoreProducts,
    filteredProducts.length,
  ]);

  useEffect(() => {
    if (showSkeletonLoading) {
      prevLoadedCountRef.current = 0;
      wasLoadingMoreRef.current = false;
      setLoadMoreCompleteMessage("");
      return;
    }

    if (productSearchLoadingMore) {
      wasLoadingMoreRef.current = true;
      setLoadMoreCompleteMessage("");
      return;
    }

    if (wasLoadingMoreRef.current && filteredProducts.length > prevLoadedCountRef.current) {
      const loaded = filteredProducts.length;
      const total = productSearchTotalCount ?? loaded;
      setLoadMoreCompleteMessage(
        total === 1 ? t("1 product loaded") : t("{{loaded}} of {{total}} products loaded", { loaded, total }),
      );
      wasLoadingMoreRef.current = false;
    }

    prevLoadedCountRef.current = filteredProducts.length;
  }, [filteredProducts.length, productSearchLoadingMore, productSearchTotalCount, showSkeletonLoading, t]);

  const infiniteScrollLiveMessage = useMemo(() => {
    if (viewMode === "review" || showSkeletonLoading) {
      return "";
    }

    if (productSearchSelectAllPending) {
      return t("Selecting all products…");
    }

    if (productSearchLoadingMore) {
      return t("Loading more products…");
    }

    return loadMoreCompleteMessage;
  }, [
    viewMode,
    loadMoreCompleteMessage,
    productSearchLoadingMore,
    productSearchSelectAllPending,
    showSkeletonLoading,
    t,
  ]);

  const isBrowseTableBusy = isRefreshing || productSearchLoadingMore || productSearchSelectAllPending;

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }

      if (viewMode === "review") {
        event.preventDefault();
        handleSaveAndGoBack();
        return;
      }

      if (browseOpen) {
        event.preventDefault();
        event.stopPropagation();
        setBrowseOpen(false);
        return;
      }

      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [open, viewMode, browseOpen, onClose]);

  function handleAdd() {
    const products =
      viewMode === "review" ? productsToAdd.filter(product => !reviewUncheckedIds.has(product.id)) : productsToAdd;

    if (viewMode === "review") {
      setReviewUncheckedIds(new Set());
    }

    onAdd(products);
  }

  function renderBrowseTableBody() {
    if (showSkeletonLoading) {
      return skeletonRowKeys.map(key => (
        <AddFolderProductsSkeletonRow
          key={key}
          showQuantity={showQuantity}
          showPrice={showPrice}
          showStatus={showSlotUsage}
        />
      ));
    }

    if (filteredProducts.length === 0) {
      const trimmedSearch = search.trim();

      if (trimmedSearch) {
        return (
          <tr className="add-folder-products-modal__empty-row">
            <td colSpan={browseTableColumns.length} className="add-folder-products-modal__empty-cell">
              <MessageDisplay
                icon="magnifying-glass"
                title={t("No result found")}
                content={t('Your search "{{search}}" did not match any products.\nPlease try a different search.', {
                  search: trimmedSearch,
                })}
                actions={[{ label: t("Clear search"), type: "primary", action: () => onSearchChange("") }]}
              />
            </td>
          </tr>
        );
      }

      const browseEmptyTypeLabel = getBrowseEmptyTypeLabel(browseFilter.type, t);

      if (browseEmptyTypeLabel) {
        return (
          <tr className="add-folder-products-modal__empty-row">
            <td colSpan={browseTableColumns.length} className="add-folder-products-modal__empty-cell">
              <MessageDisplay
                icon="magnifying-glass"
                title={t("No result found")}
                content={t(
                  "Assign a {{type}} to an existing product or create a new one to start browsing by {{type}}.",
                  { type: browseEmptyTypeLabel },
                )}
                actions={
                  onGoToProducts ? [{ label: t("Go to products"), type: "primary", action: onGoToProducts }] : undefined
                }
              />
            </td>
          </tr>
        );
      }

      return (
        <tr className="add-folder-products-modal__empty-row">
          <td colSpan={browseTableColumns.length} className="add-folder-products-modal__empty-cell">
            <MessageDisplay
              icon="circle-exclamation"
              title={t("No products available")}
              content={t("There aren't any products set up for this account. Create a new product to add one.")}
              actions={
                onCreateProduct
                  ? [{ label: t("Create new product"), type: "primary", action: onCreateProduct }]
                  : undefined
              }
            />
          </td>
        </tr>
      );
    }

    return (
      <>
        {filteredProducts.map((product, index) => {
          const inFolder = isProductInFolder(product);
          const selected = isProductSelected(product);
          const displayVariants = getDisplayVariants(product);
          const multiVariant = enableVariantSelection && displayVariants.length > 1;
          const isExpanded = isProductExpanded(product.id);
          const someSelected = enableVariantSelection && areSomeAvailableVariantsSelected(product);
          const isLoadMoreAnchor =
            hasMoreProducts && !productSearchLoadingMore && index === filteredProducts.length - 1;
          const secondaryLabel = multiVariant
            ? getVariantCountLabel(displayVariants.length)
            : getProductHierarchyLabel(product);

          return (
            <React.Fragment key={product.id}>
              <tr
                ref={isLoadMoreAnchor ? loadMoreSentinelRef : undefined}
                className={`add-folder-products-modal__product-data-row${inFolder ? "" : " clickable"}`}
                tabIndex={inFolder ? undefined : 0}
                aria-disabled={inFolder || undefined}
                aria-selected={inFolder ? undefined : selected}
                onClick={() => handleRowClick(product)}
                onKeyDown={
                  inFolder ? undefined : event => handleActivationKeyDown(event, () => handleRowClick(product))
                }
              >
                <td>
                  <div className="add-folder-products-modal__product-row">
                    <span aria-hidden="true">
                      <Checkbox
                        size="medium"
                        checked={selected || inFolder}
                        indeterminate={someSelected}
                        readOnly
                        disabled={inFolder}
                        tabIndex={-1}
                      />
                    </span>
                    <div className="add-folder-products-modal__product-row-text">
                      <div className="add-folder-products-modal__product-name-row">
                        <div className="add-folder-products-modal__product-name">{getProductDisplayTitle(product)}</div>
                        {multiVariant ? (
                          <button
                            type="button"
                            className="add-folder-products-modal__chevron"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? t("Collapse") : t("Expand")}
                            onClick={event => {
                              event.stopPropagation();
                              toggleProductExpanded(product.id);
                            }}
                          >
                            <FontAwesomeIcon icon={isExpanded ? ["far", "chevron-up"] : ["far", "chevron-down"]} />
                          </button>
                        ) : null}
                      </div>
                      <div className="add-folder-products-modal__product-label">{secondaryLabel}</div>
                    </div>
                  </div>
                </td>
                <td className="add-folder-products-modal__type-cell" title={product.type}>
                  <div>{product.type}</div>
                </td>
                {showPrice ? (
                  <td className="add-folder-products-modal__price-cell">{renderProductPrice(product)}</td>
                ) : null}
                {showQuantity ? (
                  <td
                    className={onQuantityChange ? "add-folder-products-modal__qty-cell-input" : ""}
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}
                  >
                    {onQuantityChange ? (
                      (selected || someSelected) && !inFolder ? (
                        <Input
                          type="number"
                          labelHidden
                          value={String(productQuantities[product.id] ?? 1)}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            onQuantityChange(product.id, Math.max(1, Number(event.target.value) || 1));
                          }}
                        />
                      ) : null
                    ) : (
                      productQuantity(product)
                    )}
                  </td>
                ) : null}
                {showSlotUsage ? (
                  <td className="add-folder-products-modal__badge-cell">
                    {inFolder ? (
                      <Badge type="success" size="small" outline>
                        {t("Added")}
                      </Badge>
                    ) : null}
                  </td>
                ) : null}
              </tr>
              {multiVariant && isExpanded
                ? displayVariants.map(variant => {
                    const alreadyAdded = isVariantAlreadyAdded(variant.id);
                    const variantSelected = isVariantSelected(product, variant.id);
                    const variantDisabled = alreadyAdded || inFolder;

                    return (
                      <tr
                        key={variant.id}
                        className={`add-folder-products-modal__variant-row${variantDisabled ? "" : " clickable"}`}
                        tabIndex={variantDisabled ? undefined : 0}
                        aria-disabled={variantDisabled || undefined}
                        aria-selected={variantSelected}
                        onClick={() => handleVariantRowClick(product, variant)}
                        onKeyDown={
                          variantDisabled
                            ? undefined
                            : event => handleActivationKeyDown(event, () => handleVariantRowClick(product, variant))
                        }
                      >
                        <td>
                          <div className="add-folder-products-modal__product-row add-folder-products-modal__product-row--variant">
                            <span aria-hidden="true">
                              <Checkbox
                                size="medium"
                                checked={variantSelected}
                                readOnly
                                disabled={variantDisabled}
                                tabIndex={-1}
                              />
                            </span>
                            <div className="add-folder-products-modal__product-row-text">
                              <div className="add-folder-products-modal__product-name">{variant.title}</div>
                            </div>
                          </div>
                        </td>
                        <td className="add-folder-products-modal__type-cell" />
                        {showPrice ? <td className="add-folder-products-modal__price-cell" /> : null}
                        {showQuantity ? (
                          <td className={`add-folder-products-modal__qty-cell${onQuantityChange ? "" : " add-folder-products-modal__qty-cell--readonly"}`}>
                            {onQuantityChange ? null : variantQuantity(variant)}
                          </td>
                        ) : null}
                        {showSlotUsage ? (
                          <td className="add-folder-products-modal__badge-cell">
                            {alreadyAdded ? (
                              <Badge type="success" size="small" outline>
                                {t("Added")}
                              </Badge>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                : null}
            </React.Fragment>
          );
        })}
        {productSearchLoadingMore
          ? loadMoreSkeletonRowKeys.map(key => (
              <AddFolderProductsSkeletonRow
                key={key}
                showQuantity={showQuantity}
                showPrice={showPrice}
                showStatus={showSlotUsage}
              />
            ))
          : null}
      </>
    );
  }

  const selectedCount = productsToAdd.length;
  const isReviewMode = viewMode === "review";
  const reviewCheckedCount = productsToAdd.filter(product => isReviewChecked(product)).length;
  const activeCount = isReviewMode ? reviewCheckedCount : selectedCount;
  const okText =
    confirmText ??
    (activeCount === 0
      ? t("Add products")
      : activeCount === 1
      ? t("Add 1 product")
      : t("Add {{count}} products", { count: activeCount }));

  const liveRegionMessage = useMemo(() => {
    if (isReviewMode) {
      return reviewCheckedCount === 1
        ? t("1 product selected for review")
        : t("{{count}} products selected for review", { count: reviewCheckedCount });
    }

    if (showSkeletonLoading) {
      return t("Loading products…");
    }

    if (isRefreshing) {
      return t("Refreshing products…");
    }

    const selectionMessage =
      selectedCount === 1 ? t("1 product selected") : t("{{count}} products selected", { count: selectedCount });
    const totalCount = productSearchTotalCount ?? filteredProducts.length;
    const totalMessage = totalCount === 1 ? t("1 product total") : t("{{count}} products total", { count: totalCount });

    return `${selectionMessage}. ${totalMessage}.`;
  }, [
    filteredProducts.length,
    isRefreshing,
    isReviewMode,
    productSearchTotalCount,
    reviewCheckedCount,
    selectedCount,
    showSkeletonLoading,
    t,
  ]);

  function handleVendorFilterToggle() {
    onVendorFilterChange?.(!vendorFilterEnabled);
  }

  return (
    <Sheet
      closable
      onClose={onClose}
      onCancel={isReviewMode ? handleSaveAndGoBack : onClose}
      backDropCancel={!isReviewMode}
      size="extra-wide"
      open={open}
      cancelText={isReviewMode ? t("Save and go back") : t("Close")}
      okText={okText}
      okDisabled={activeCount === 0 || okLoading}
      okLoading={okLoading}
      title={isReviewMode ? t("Review selection") : (title ?? t("Add products"))}
      supportText={
        isReviewMode
          ? onQuantityChange
            ? t("Set quantities and click on any product row to remove it.")
            : t("Click on any product row in the table to remove it.")
          : (supportText ?? t("Search or browse products by department, category, or subcategory to add them to the folder."))
      }
      onOk={handleAdd}
    >
      <div className="add-folder-products-modal">
        <div className="add-folder-products-modal__visually-hidden" aria-live="polite" aria-atomic="true">
          {liveRegionMessage}
        </div>
        <div className="add-folder-products-modal__visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {infiniteScrollLiveMessage}
        </div>
        {isReviewMode ? (
          <>
            <div className="add-folder-products-modal__review-toolbar">
              <button
                type="button"
                className="add-folder-products-modal__remove-all"
                onClick={handleReviewClearAll}
                disabled={selectedCount === 0 || reviewUncheckedIds.size === selectedCount}
              >
                <Icon icon={faTrash} size="medium" />
                <span>{t("Remove all")}</span>
              </button>
              <span className="add-folder-products-modal__total-count">
                {selectedCount === 1
                  ? `${t("Total")} 1 ${t("product")}`
                  : `${t("Total")} ${selectedCount} ${t("products")}`}
              </span>
            </div>
            <div className="add-folder-products-modal__table-scroll">
              <div className="add-folder-products-modal__table-scroll-x">
                <DataTable
                  className="add-folder-products-modal__table add-folder-products-modal__table--review"
                  columns={browseTableColumns}
                >
                  {productsToAdd.map(product => {
                    const reviewChecked = isReviewChecked(product);

                    return (
                      <tr
                        key={product.id}
                        className="clickable"
                        tabIndex={0}
                        aria-selected={reviewChecked}
                        onClick={() => handleReviewRowClick(product)}
                        onKeyDown={event => handleActivationKeyDown(event, () => handleReviewRowClick(product))}
                      >
                        <td>
                          <div className="add-folder-products-modal__product-row">
                            <span aria-hidden="true">
                              <Checkbox size="medium" checked={reviewChecked} readOnly tabIndex={-1} />
                            </span>
                            <div className="add-folder-products-modal__product-row-text">
                              <div className="add-folder-products-modal__product-name">
                                {getProductDisplayTitle(product)}
                              </div>
                              <div className="add-folder-products-modal__product-label">
                                {enableVariantSelection && (product.variants?.length ?? 0) > 1
                                  ? getVariantCountLabel(product.variants?.length ?? 0)
                                  : getProductHierarchyLabel(product)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="add-folder-products-modal__type-cell" title={product.type}>
                          <div>{product.type}</div>
                        </td>
                        {showPrice ? (
                          <td className="add-folder-products-modal__price-cell">{renderProductPrice(product)}</td>
                        ) : null}
                        {showQuantity ? (
                          <td
                            className={`add-folder-products-modal__qty-cell${onQuantityChange ? "" : " add-folder-products-modal__qty-cell--readonly"}`}
                            onClick={event => event.stopPropagation()}
                            onKeyDown={event => event.stopPropagation()}
                          >
                            {onQuantityChange ? (
                              <Input
                                type="number"
                                labelHidden
                                value={String(productQuantities[product.id] ?? 1)}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  onQuantityChange(product.id, Math.max(1, Number(event.target.value) || 1));
                                }}
                              />
                            ) : (
                              productQuantity(product)
                            )}
                          </td>
                        ) : null}
                        {showSlotUsage ? <td className="add-folder-products-modal__badge-cell" /> : null}
                      </tr>
                    );
                  })}
                </DataTable>
              </div>
            </div>
          </>
        ) : (
          <div className="add-folder-products-modal__layout">
            <div
              className={`add-folder-products-modal__browse-shell${
                browseOpen ? " add-folder-products-modal__browse-shell--open" : ""
              }`}
            >
              <aside
                className="add-folder-products-modal__browse"
                aria-hidden={!browseOpen}
                aria-label={t("Browse products by department")}
              >
                <Button
                  type="secondary"
                  size="medium"
                  block
                  className="add-folder-products-modal__browse-toggle"
                  onClick={() => setBrowseOpen(false)}
                >
                  <span className="add-folder-products-modal__browse-open-inner">
                    <Icon icon={faSidebar} size="medium" />
                    {t("Hide browse")}
                  </span>
                </Button>
                {browseOpen ? (
                  <ProductBrowseTree
                    departments={departments}
                    departmentsLoading={departmentsLoading}
                    uncategorizedCount={uncategorizedCount}
                    browseFilter={browseFilter}
                    onBrowseFilterChange={onBrowseFilterChange}
                    onGoToProducts={onGoToProducts}
                  />
                ) : null}
              </aside>
            </div>
            <div className="add-folder-products-modal__main">
              <div className="add-folder-products-modal__sticky-header">
                <div
                  className={`add-folder-products-modal__slots-row${
                    slotsHidden ? " add-folder-products-modal__slots-row--no-usage" : ""
                  }`}
                >
                  {!slotsHidden ? (
                    <div className="add-folder-products-modal__slots-count">{`${usedSlots} / ${totalSlots} ${t(
                      "slots used",
                    )}`}</div>
                  ) : null}
                  <div className="add-folder-products-modal__selection-actions">
                    <span className="add-folder-products-modal__selection-count">
                      {`${selectedCount} ${selectedCount === 1 ? t("product selected") : t("products selected")}`}
                    </span>
                    {selectedCount > 0 ? (
                      <>
                        <span className="add-folder-products-modal__selection-actions-divider" aria-hidden="true" />
                        <Button type="link" size="small" onClick={handleOpenReview}>
                          {t("View selection")}
                        </Button>
                        <span className="add-folder-products-modal__selection-actions-divider" aria-hidden="true" />
                        <Button type="link" size="small" onClick={handleClearAll}>
                          {t("Clear all")}
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
                {showSlotsCapacityCallout ? (
                  <Callout
                    type="info"
                    iconAlignTop
                    title={
                      isFolderCapacityFull
                        ? t("Folder capacity is full")
                        : remainingEmptySlots === 1
                        ? t("1 slot open")
                        : t("{{count}} slots open", { count: remainingEmptySlots })
                    }
                    content={
                      isFolderCapacityFull
                        ? t("Remove products from your selection before adding more.")
                        : remainingEmptySlots === 1
                        ? t("You can only add 1 more product to this slot.")
                        : t("You can only add {{count}} more products to this slot.", {
                            count: remainingEmptySlots,
                          })
                    }
                  />
                ) : null}
                <div
                  className={`add-folder-products-modal__search${
                    browseOpen ? " add-folder-products-modal__search--panel-open" : ""
                  }`}
                >
                  {!browseOpen ? (
                    <Button
                      type="secondary"
                      size="medium"
                      className="add-folder-products-modal__browse-open"
                      onClick={() => setBrowseOpen(true)}
                    >
                      <span className="add-folder-products-modal__browse-open-inner">
                        <Icon icon={faSidebar} size="medium" />
                        {t("Browse products")}
                      </span>
                    </Button>
                  ) : null}
                  <label htmlFor={searchInputId} className="add-folder-products-modal__visually-hidden">
                    {t("Search products")}
                  </label>
                  <Input
                    id={searchInputId}
                    inputRef={searchInputRef}
                    value={search}
                    onChange={(value: React.ChangeEvent<HTMLInputElement>) => onSearchChange(value.target.value)}
                    onSearchClear={() => onSearchChange("")}
                    type="search"
                    labelHidden
                    placeholder={t("Search by product name, department, category or subcategory…")}
                  />
                  {vendorId ? (
                    <div>
                      <Checkbox
                        size="small"
                        checked={vendorFilterEnabled}
                        onChange={handleVendorFilterToggle}
                        label={t("Only show products from this vendor")}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="add-folder-products-modal__table-header">
                <button
                  type="button"
                  className="add-folder-products-modal__select-all"
                  onClick={handleSelectAllToggle}
                  disabled={totalSelectable === 0 || showSkeletonLoading || productSearchSelectAllPending}
                  aria-label={t("Select all products")}
                  aria-busy={productSearchSelectAllPending || undefined}
                >
                  <span aria-hidden="true">
                    <Checkbox
                      size="small"
                      checked={selectAllVisualChecked}
                      indeterminate={selectAllVisualIndeterminate}
                      readOnly
                      disabled={totalSelectable === 0 || showSkeletonLoading}
                      tabIndex={-1}
                    />
                  </span>
                  <span className="add-folder-products-modal__select-all-label">{t("Select all")}</span>
                </button>
                <span
                  className={`add-folder-products-modal__total-count${
                    isRefreshing ? " add-folder-products-modal__total-count--refreshing" : ""
                  }`}
                >
                  {showSkeletonLoading ? (
                    <SkeletonBar className="add-folder-products-modal__total-count-skeleton" />
                  ) : (productSearchTotalCount ?? filteredProducts.length) === 1 ? (
                    `${t("Total")} 1 ${t("product")}`
                  ) : (
                    `${t("Total")} ${productSearchTotalCount ?? filteredProducts.length} ${t("products")}`
                  )}
                </span>
              </div>
              <div
                className={`add-folder-products-modal__table-scroll${
                  isTableEmpty ? " add-folder-products-modal__table-scroll--empty" : ""
                }`}
              >
                <div
                  className="add-folder-products-modal__table-scroll-x"
                  ref={tableScrollRef}
                  role={!isTableEmpty && !showSkeletonLoading ? "region" : undefined}
                  aria-label={!isTableEmpty && !showSkeletonLoading ? t("Product results") : undefined}
                  aria-busy={isBrowseTableBusy || undefined}
                >
                  <DataTable
                    className={`add-folder-products-modal__table${
                      isTableEmpty ? " add-folder-products-modal__table--empty" : ""
                    }${showSkeletonLoading ? " add-folder-products-modal__table--skeleton-loading" : ""}`}
                    columns={browseTableColumns}
                    loading={false}
                    tableBodyClassName={
                      isRefreshing
                        ? "add-folder-products-modal__table-tbody--refreshing"
                        : isTableEmpty
                        ? "add-folder-products-modal__table-tbody--empty"
                        : undefined
                    }
                    ariaBusy={isBrowseTableBusy}
                  >
                    {renderBrowseTableBody()}
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}
